/**
 * YAML frontmatter support for the Tiptap markdown editor.
 *
 * Skill docs (`.agents/skills/<name>/SKILL.md`) lead with a `---`-fenced YAML
 * block. Fed verbatim to the markdown parser (`@tiptap/markdown`, marked
 * under the hood), the opening `---` parses as a horizontal rule and the
 * closing `---` as a setext heading underline — the block renders as mangled
 * prose. This extension lifts the block out of the markdown stream entirely
 * and represents it as a single collapsible, editable node instead.
 *
 * Strategy (mirrors `wiki-link.ts`: keep custom markdown parsing out of it):
 *
 * - **Load** — the consumer (`MarkdownEditor`) splits the leading frontmatter
 *   off the document text with {@link splitFrontmatter} *before* handing the
 *   body to the markdown parser, then prepends a `frontmatter` node carrying
 *   the raw YAML as an attribute. The parser never sees the `---`, so it can't
 *   mangle it.
 * - **Save** — the node's `renderMarkdown` hook re-emits `---\n<raw>\n---`
 *   at the top of the document, so `getMarkdown()` round-trips it. Untouched
 *   frontmatter serializes byte-for-byte (the raw string is the source of
 *   truth); only fields the user actually edits are re-stringified.
 *
 * The editing model is deliberately line-based, not a YAML AST: top-level
 * scalars, `- item` list entries, and one-level `key: value` nested scalars
 * are editable rows; a `key:` that introduces children is a "block" row
 * (key-only — deleting it removes the children too); everything else
 * (comments, deeper nesting, block-scalar text) passes through verbatim as
 * opaque rows. Lines inside a `|`/`>` block scalar are literal text and stay
 * opaque even when they look like YAML.
 *
 * Security note: the *read* path is a dumb line splitter — it can't execute
 * code, instantiate objects, or expand anchors (the dangerous YAML features it
 * simply doesn't implement). The genuinely new surface is the *write* path:
 * {@link escapeScalar} quotes any value that could otherwise break out of its
 * single-line scalar (newlines, leading indicators, `:` / `#`) so an edited
 * value can never inject an extra key or prematurely close the `---` fence.
 */
import { Node, mergeAttributes } from "@tiptap/core";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";
import { ChevronRight, Plus, X } from "lucide-react";
import {
  escapeScalar,
  parseScalar,
  sanitizeKey,
} from "playground/skills/scalar";
import { useCallback, useEffect, useRef, useState } from "react";

// Re-exported so the editor's frontmatter API is a single import for
// consumers/tests; the codec itself is shared with the skill registry parser
// (`playground/skills/frontmatter.ts`) so editor output and catalog input
// agree on quoting/escaping.
export { escapeScalar, parseScalar, sanitizeKey };

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit testing; no Tiptap/React dependency).
// ---------------------------------------------------------------------------

export interface FrontmatterField {
  /** Top-level `key: value` scalar. Editable key + value. */
  kind: "field";
  key: string;
  value: string;
}
export interface FrontmatterBlock {
  /**
   * Top-level `key:` introducing indented children (a list or a nested map),
   * or a block scalar (`key: |` / `key: >`). Editable key, no value — the
   * children are their own rows. `rest` is the verbatim text after the colon
   * (e.g. "" or " |") so renaming the key preserves a block-scalar indicator.
   */
  kind: "block";
  key: string;
  rest: string;
}
export interface FrontmatterItem {
  /** Indented `- value` list item (any depth). Editable value. */
  kind: "item";
  indent: string;
  value: string;
}
export interface FrontmatterNested {
  /** Indented `key: value` scalar inside a nested map. Editable value. */
  kind: "nested";
  indent: string;
  key: string;
  value: string;
}
export interface FrontmatterOpaque {
  kind: "opaque";
  /** A line we don't model as an editable scalar — preserved verbatim:
   *  comments, sub-block intros (`  list:`), block-scalar text, etc. */
  text: string;
}
export type FrontmatterRow =
  | FrontmatterField
  | FrontmatterBlock
  | FrontmatterItem
  | FrontmatterNested
  | FrontmatterOpaque;

// Leading `---`-fenced block, anchored at the very start of the document. The
// content group is lazy so it stops at the *first* closing fence. An opening
// `---` with no matching close (a genuine horizontal rule) simply doesn't
// match, leaving the text untouched.
const FRONTMATTER_RE = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

/**
 * Split a leading YAML frontmatter block off the document text. Returns the
 * raw YAML (the text *between* the fences, no `---`) and the remaining body.
 * When there's no frontmatter, `raw` is `null` and `body` is the input
 * unchanged.
 */
export function splitFrontmatter(text: string): {
  raw: string | null;
  body: string;
} {
  const match = FRONTMATTER_RE.exec(text);
  if (!match) return { raw: null, body: text };
  // Drop the conventional single blank line separating the fence from the
  // body. Markdown ignores leading blank lines anyway, so this just keeps the
  // body clean for the parser.
  const body = text.slice(match[0].length).replace(/^\r?\n/, "");
  return { raw: match[1] ?? "", body };
}

// Unindented `key: value` scalar. Indented / colon-less lines fall through to
// the indented row kinds (item / nested) or opaque passthrough.
const FIELD_RE = /^([A-Za-z0-9_][A-Za-z0-9_-]*):(.*)$/;
// Indented `- value` list item (or a bare `-`).
const ITEM_RE = /^([ \t]+)-(?: (.*))?$/;
// Indented `key: value` nested-map scalar. Requires a non-empty value —
// an indented `key:` is a sub-block intro and stays opaque.
const NESTED_RE = /^([ \t]+)([A-Za-z0-9_][A-Za-z0-9_-]*): (.+)$/;
// `|` / `>` block-scalar indicator (with optional chomping modifier).
const BLOCK_SCALAR_RE = /^[|>][+-]?$/;

/**
 * Parse raw YAML into ordered rows. Every input line maps to exactly one row
 * so nothing is lost. Top-level scalars are editable fields; a `key:` with
 * indented children (or a `key: |` block scalar) is a block whose children
 * parse as editable list items / nested scalars. Lines *inside* a block
 * scalar are literal text, not YAML, so they stay opaque even when they look
 * like list items — editing them as YAML would rewrite the literal content.
 */
export function parseRows(raw: string): FrontmatterRow[] {
  if (raw === "") return [];
  const lines = raw.split("\n");
  const rows: FrontmatterRow[] = [];
  // While inside a block scalar: the indent length of the introducing key.
  // Deeper-indented (and blank) lines are literal content.
  let literalIndent: number | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (literalIndent !== null) {
      const indent = /^[ \t]*/.exec(line)![0].length;
      if (line.trim() === "" || indent > literalIndent) {
        rows.push({ kind: "opaque", text: line });
        continue;
      }
      literalIndent = null;
    }

    const top = FIELD_RE.exec(line);
    if (top) {
      const rest = top[2]!;
      const value = parseScalar(rest);
      if (BLOCK_SCALAR_RE.test(rest.trim())) {
        rows.push({ kind: "block", key: top[1]!, rest });
        literalIndent = 0;
        continue;
      }
      const next = lines[i + 1];
      const hasChildren =
        value === "" && next !== undefined && /^[ \t]+\S/.test(next);
      if (hasChildren) {
        rows.push({ kind: "block", key: top[1]!, rest });
      } else {
        rows.push({ kind: "field", key: top[1]!, value });
      }
      continue;
    }

    const item = ITEM_RE.exec(line);
    if (item) {
      rows.push({
        kind: "item",
        indent: item[1]!,
        value: parseScalar(item[2] ?? ""),
      });
      continue;
    }

    const nested = NESTED_RE.exec(line);
    if (nested) {
      if (BLOCK_SCALAR_RE.test(nested[3]!.trim())) {
        // A nested block scalar (`  body: |`): keep the intro line opaque and
        // treat everything deeper as literal text.
        rows.push({ kind: "opaque", text: line });
        literalIndent = nested[1]!.length;
        continue;
      }
      rows.push({
        kind: "nested",
        indent: nested[1]!,
        key: nested[2]!,
        value: parseScalar(nested[3]!),
      });
      continue;
    }

    rows.push({ kind: "opaque", text: line });
  }
  return rows;
}

/** Re-emit rows as raw YAML. Field/block rows with a blank key are dropped
 *  (an in-progress "add field"); opaque rows pass through verbatim. */
export function serializeRows(rows: FrontmatterRow[]): string {
  return rows
    .filter(
      (row) =>
        row.kind === "opaque" || row.kind === "item" || row.key.trim() !== "",
    )
    .map((row) => {
      switch (row.kind) {
        case "opaque":
          return row.text;
        case "block":
          // `rest` is verbatim (e.g. "" or " |") so block-scalar indicators
          // survive a key rename.
          return `${row.key}:${row.rest}`;
        case "item":
          return row.value === ""
            ? `${row.indent}-`
            : `${row.indent}- ${escapeScalar(row.value)}`;
        case "nested":
          return `${row.indent}${row.key}: ${escapeScalar(row.value)}`;
        case "field":
          // A bare `key:` (empty value) stays a bare `key:` rather than
          // flattening to `key: ""`.
          if (row.value === "") return `${row.key}:`;
          return `${row.key}: ${escapeScalar(row.value)}`;
      }
    })
    .join("\n");
}

/**
 * Number of rows the block at `start` spans: the block row itself plus its
 * children — every following row up to the next top-level row (field, block,
 * or unindented non-blank opaque). Used so deleting `tags:` removes its list
 * items instead of orphaning them. Returns 1 for non-block rows.
 */
export function blockExtent(rows: FrontmatterRow[], start: number): number {
  if (rows[start]?.kind !== "block") return 1;
  let end = start + 1;
  while (end < rows.length) {
    const row = rows[end]!;
    if (row.kind === "field" || row.kind === "block") break;
    if (
      row.kind === "opaque" &&
      row.text.trim() !== "" &&
      !/^[ \t]/.test(row.text)
    ) {
      break;
    }
    end++;
  }
  return end - start;
}

// ---------------------------------------------------------------------------
// Tiptap node + React node view
// ---------------------------------------------------------------------------

/**
 * Atom node holding a frontmatter block as a single `raw` (YAML text) attr.
 * It's `atom: true` so it has no ProseMirror-editable content — all editing
 * flows through the React node view's form, which keeps the YAML structure
 * intact instead of letting it be mangled as free text.
 */
export const Frontmatter = Node.create({
  name: "frontmatter",
  group: "block",
  atom: true,
  selectable: false,
  draggable: false,
  isolating: true,

  addAttributes() {
    return {
      raw: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-frontmatter-raw") ?? "",
        renderHTML: (attrs) => ({
          "data-frontmatter-raw": attrs.raw as string,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-frontmatter]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-frontmatter": "" })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FrontmatterNodeView);
  },

  // Consumed by `@tiptap/markdown`'s serializer so `getMarkdown()` re-emits
  // the fenced block at the top of the document. Parsing stays at the editor
  // boundary ({@link splitFrontmatter}) — the block is lifted out *before*
  // the markdown parser runs, so no tokenizer is registered here.
  renderMarkdown(node) {
    const raw = String(node.attrs?.raw ?? "").replace(/\r?\n+$/, "");
    return `---\n${raw}\n---`;
  },
});

function FrontmatterNodeView({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) {
  const raw = (node.attrs.raw as string) ?? "";
  const editable = editor.isEditable;
  const [expanded, setExpanded] = useState(false);

  // Single source of truth is `node.attrs.raw`, but we mirror it into local
  // state for snappy, focus-preserving edits: writing through
  // `updateAttributes` on every keystroke would round-trip a ProseMirror
  // transaction and risk caret jumps. We re-seed only when `raw` changes from
  // *outside* (a peer/agent edit), detected via `lastRawRef`.
  const [rows, setRows] = useState<FrontmatterRow[]>(() => parseRows(raw));
  const lastRawRef = useRef(raw);
  useEffect(() => {
    if (raw !== lastRawRef.current) {
      setRows(parseRows(raw));
      lastRawRef.current = raw;
    }
  }, [raw]);

  const commit = useCallback(
    (next: FrontmatterRow[]) => {
      setRows(next);
      const nextRaw = serializeRows(next);
      lastRawRef.current = nextRaw;
      updateAttributes({ raw: nextRaw });
    },
    [updateAttributes],
  );

  const updateRow = (index: number, patch: { key?: string; value?: string }) =>
    commit(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  // A block takes its children with it; every other row removes just itself.
  const removeRow = (index: number) => {
    const extent = blockExtent(rows, index);
    commit(rows.filter((_, i) => i < index || i >= index + extent));
  };
  const addField = () =>
    commit([...rows, { kind: "field", key: "", value: "" }]);

  const summary = rows
    .filter(
      (r): r is FrontmatterField | FrontmatterBlock =>
        r.kind === "field" || r.kind === "block",
    )
    .map((r) => r.key)
    .join(", ");

  return (
    <NodeViewWrapper
      className="not-prose my-2 rounded-md border border-border bg-muted/40 text-sm"
      contentEditable={false}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-muted-foreground hover:text-foreground"
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
        />
        <span className="font-medium">Frontmatter</span>
        {!expanded && summary ? (
          <span className="truncate text-xs opacity-70">{summary}</span>
        ) : null}
      </button>

      {expanded ? (
        <div className="space-y-1.5 border-t border-border px-3 py-2">
          {rows.map((row, i) => {
            if (row.kind === "field" || row.kind === "block") {
              return (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={row.key}
                    disabled={!editable}
                    placeholder="key"
                    onKeyDown={(e) => e.stopPropagation()}
                    onPaste={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      updateRow(i, { key: sanitizeKey(e.target.value) })
                    }
                    className="w-40 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-xs outline-none focus:border-ring"
                  />
                  <span className="text-muted-foreground">:</span>
                  {row.kind === "field" ? (
                    <input
                      value={row.value}
                      disabled={!editable}
                      placeholder="value"
                      onKeyDown={(e) => e.stopPropagation()}
                      onPaste={(e) => e.stopPropagation()}
                      onChange={(e) => updateRow(i, { value: e.target.value })}
                      className="flex-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-xs outline-none focus:border-ring"
                    />
                  ) : (
                    // A block key has no scalar of its own — its children are
                    // the rows below. No value input to mistype into.
                    <span className="flex-1" />
                  )}
                  {editable ? (
                    <button
                      type="button"
                      aria-label={
                        row.kind === "block"
                          ? `Remove ${row.key || "block"} and its contents`
                          : `Remove ${row.key || "field"}`
                      }
                      onClick={() => removeRow(i)}
                      className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              );
            }
            if (row.kind === "item" || row.kind === "nested") {
              return (
                <div
                  key={i}
                  className="flex items-center gap-2"
                  style={{ paddingLeft: `${row.indent.length}ch` }}
                >
                  {row.kind === "item" ? (
                    <span className="font-mono text-xs text-muted-foreground">
                      -
                    </span>
                  ) : (
                    <span className="font-mono text-xs text-muted-foreground">
                      {row.key}:
                    </span>
                  )}
                  <input
                    value={row.value}
                    disabled={!editable}
                    placeholder="value"
                    onKeyDown={(e) => e.stopPropagation()}
                    onPaste={(e) => e.stopPropagation()}
                    onChange={(e) => updateRow(i, { value: e.target.value })}
                    className="flex-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-xs outline-none focus:border-ring"
                  />
                  {editable ? (
                    <button
                      type="button"
                      aria-label={
                        row.kind === "item"
                          ? "Remove list item"
                          : `Remove ${row.key}`
                      }
                      onClick={() => removeRow(i)}
                      className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              );
            }
            return row.text.trim() ? (
              // Preserve (read-only) lines we don't model — comments,
              // sub-block intros, block-scalar text — so editing a scalar
              // never silently drops them.
              <div
                key={i}
                className="whitespace-pre-wrap font-mono text-xs text-muted-foreground opacity-60"
              >
                {row.text}
              </div>
            ) : null;
          })}
          {editable ? (
            <button
              type="button"
              onClick={addField}
              className="flex items-center gap-1 pt-0.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              Add field
            </button>
          ) : null}
        </div>
      ) : null}
    </NodeViewWrapper>
  );
}
